import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Box } from '~/eds/src/components/Box/Box';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { getFieldsBySectionId, getSectionById } from '~/reducers/intakeForm';
import {
  getAnsweredFieldCount,
  getEnabledFieldCount,
} from '~/utils/intakeForm';

import { Field, IntakeFormState, WorkflowFormSectionProps } from './types';
import WorkflowFormQuestion from './WorkflowFormQuestion';
import WorkflowFormSectionHeader from './WorkflowFormSectionHeader';

interface RootState {
  intakeForm: IntakeFormState;
}

const NON_RENDERED_TYPES = [
  WorkflowFieldType.DateSigned,
  WorkflowFieldType.Signature,
  WorkflowFieldType.Initials,
];

const WorkflowFormSection = ({
  disabled,
  sectionId,
}: WorkflowFormSectionProps) => {
  const section = useSelector((state: RootState) =>
    getSectionById(state.intakeForm, sectionId),
  );
  const fields = useSelector((state: RootState) =>
    getFieldsBySectionId(state.intakeForm, sectionId),
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const intl = useIntl();

  const renderableFields = useMemo(
    () =>
      fields.filter((field: Field) => !NON_RENDERED_TYPES.includes(field.type)),
    [fields],
  );

  const { enabledFieldCount, answeredFieldCount } = useMemo(() => {
    const enabled = getEnabledFieldCount(renderableFields);
    const answered = getAnsweredFieldCount(renderableFields);
    return { enabledFieldCount: enabled, answeredFieldCount: answered };
  }, [renderableFields]);

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  if (!enabledFieldCount) return null;

  const headerContent = !disabled && (
    <Layout align="center" spacing={2} flexShrink={0}>
      {answeredFieldCount < enabledFieldCount ? (
        <Icon icon="status-pending" color="status.warning" size="m" />
      ) : (
        <Icon icon="check" color="status.success" size="m" />
      )}
      <Text color="text.secondary" variant="body">
        {intl.formatMessage(
          {
            id: 'WorkflowFormSection.answeredCount',
            defaultMessage:
              '{answeredFieldCount} answered ({enabledFieldCount} total)',
            description:
              'Displays the number of answered questions compared to the total number of questions in the form section.',
          },
          {
            answeredFieldCount,
            enabledFieldCount,
          },
        )}
      </Text>
    </Layout>
  );

  const { id, name, description, questions } = section;

  return (
    <Box id={id} border="border.divider" borderRadius="m">
      <WorkflowFormSectionHeader
        headerContent={headerContent}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
        title={name}
      />
      {!isCollapsed && (
        <Layout direction="column" p={4} pb={8} spacing={4}>
          {description && (
            <Text color="text.secondary" variant="body">
              {description}
            </Text>
          )}
          {questions.map((questionId: string) => (
            <WorkflowFormQuestion
              key={questionId}
              disabled={disabled}
              questionId={questionId}
            />
          ))}
        </Layout>
      )}
    </Box>
  );
};

export default WorkflowFormSection;
